import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';
import clsx from 'clsx';
import { useDispatch } from 'react-redux';

import { useAppSelector } from 'redux/store';
import { MapsFiguresAlgoliaFragment, setModalOpen } from 'redux/stores/maps-figures';
import { IActiveFilter } from 'redux/stores/subsidy';

import { searchMapsFigures } from 'api/algolia';
import { MapsFiguresFieldsFragment, MapsFiguresOverviewFieldsFragment } from 'api/graphql-types';

import { ISortOption } from 'components/sort';
import { Search } from 'components/search/search';
import FilterSidebar from 'components/filter-sidebar';
import Card from 'components/card';
import { EmptyState } from 'components/empty-state/empty-state';
import { CheckboxItem } from 'components/core/checkbox-group';
import { CheckboxesModal } from 'components/checkboxes-modal/checkboxes-modal';

import { renderDownloadLink } from 'modules/downloads/downloads';
import MobileActions from 'modules/subsidy-overview/mobile-actions';

import {
	KIND_QUERY_KEY,
	KindArrayParam,
	PAGE_QUERY_KEY,
	SEARCH_QUERY_KEY,
	SORT_QUERY_KEY,
	NewOldSortParam,
	useKind,
	usePagination,
	useSearch,
	useSort
} from 'utils/hooks/queries';
import useMapsFiguresFilters from './data/use-maps-figures-filter';
import { THEME_QUERY_KEY, ThemeArrayParam, useTheme } from 'utils/hooks/queries';

import { useDebounce } from 'utils/use-debounce';

import styles from './maps-figures-overview.module.scss';
import { GetKindOptions } from './data/constants';

export const MAPS_FIGURES_OVERVIEW_LIMIT = 15;

type FetchMapsFigures = (
	page: number,
	newSort: ISortOption,
	newSearchValue: string | null,
	themeIds: string[],
	kindIds: string[]
) => Promise<void>;

type MapsFiguresFetchProps = {
	page: number;
	sort: ISortOption;
	searchValue: string;
	themeIds: string[];
	kindIds: string[];
};

interface IMapsFiguresOverviewProps {
	module: MapsFiguresOverviewFieldsFragment;
}

const MapsFiguresOverview = (props: IMapsFiguresOverviewProps): React.JSX.Element => {
	const router = useRouter();
	const { formatMessage } = useIntl();
	const dispatch = useDispatch();

	const [query, setQuery] = useQueryParams({
		[PAGE_QUERY_KEY]: NumberParam,
		[SORT_QUERY_KEY]: NewOldSortParam,
		[SEARCH_QUERY_KEY]: StringParam,
		[THEME_QUERY_KEY]: ThemeArrayParam,
		[KIND_QUERY_KEY]: KindArrayParam
	});

	const overviewRef = useRef<HTMLDivElement>(null);
	const { mapsFigures, mapsFiguresCount, modal } = useAppSelector((state) => state.mapsFigures);
	const { themes } = useAppSelector((state) => state.themes);

	const [modalFilters, setModalFilters] = useState<CheckboxItem[]>([]);
	const [modalOptions, setModalOptions] = useState<any>();

	const [requests, setRequests] = useState<MapsFiguresAlgoliaFragment[]>(mapsFigures || []);
	const [requestIsLoading, setRequestIsLoading] = useState<boolean>(false);
	const [requestCount, setRequestCount] = useState<number>(mapsFiguresCount);

	const [sortIsOpen, setSortIsOpen] = useState<boolean>(false);
	const [filtersIsOpen, setFiltersIsOpen] = useState<boolean>(false);
	const [initialQuery, setInitialQuery] = useState<typeof query | undefined>();
	const [hasFiltered, setHasFiltered] = useState<boolean>(false);

	const { currentPage, setCurrentPage, Pagination } = usePagination(MAPS_FIGURES_OVERVIEW_LIMIT);
	const { sort, setSort, Sort } = useSort('mapsFigures', 'alfabetical');
	const { searchValue, setSearchValue } = useSearch();
	const { selectedThemes, setSelectedThemesParam } = useTheme();
	const { selectedKinds, setSelectedKindsParam } = useKind(GetKindOptions());

	const [search, setSearch] = useState<string | null>(searchValue);
	const debouncedSearch = useDebounce(search, 800);

	const fetchMapsFigures: FetchMapsFigures = async (
		page,
		newSort,
		newSearchValue,
		themeIds,
		kindIds
	): Promise<void> => {
		setRequestIsLoading(true);

		try {
			const { results, total } = await searchMapsFigures(
				newSearchValue || '',
				{
					type: kindIds[0],
					themesIds: themeIds
				},
				{
					sort: newSort.id === 'desc' ? 'desc' : 'asc',
					page,
					limit: MAPS_FIGURES_OVERVIEW_LIMIT,
					locale: router.locale
				}
			);

			setRequests(results);
			setRequestCount(total);
		} catch {
			setRequests([]);
			setRequestCount(0);
		}

		setRequestIsLoading(false);
	};

	const scrollToTop = (): void => {
		if (typeof window !== 'undefined') {
			const extraOffset = 140;
			const moduleOffset = (overviewRef.current?.getBoundingClientRect().top || 0) + window.scrollY - extraOffset;
			window.scrollTo({ top: moduleOffset, left: 0, behavior: 'smooth' });
		}
	};

	const handleDeleteFilter = (item?: IActiveFilter): void => {
		if (item?.label) {
			const newSelectedThemes = selectedThemes.filter((selection) => selection.label !== item.label);
			setSelectedThemesParam(newSelectedThemes.length > 0 ? newSelectedThemes : null);

			if (item.label === selectedKinds[0]?.label) {
				setSelectedKindsParam(null);
			}

			handleFetchMapsFigures({
				page: 1,
				themeIds: newSelectedThemes.map((newItem) => String(newItem.id)),
				kindIds: item.label === selectedKinds[0]?.label ? [] : selectedKinds.map((kind) => String(kind.id))
			});
		} else {
			setQuery({
				thema: null,
				pagina: null,
				soort: null,
				sort: null,
				zoekterm: null
			});
			setSearch('');
			handleFetchMapsFigures(undefined, true);
		}
	};

	const handleSortOnChange = (selectedSortFilter: { id: string; label: string } | undefined): void => {
		if (selectedSortFilter) {
			setSort(selectedSortFilter);
			handleFetchMapsFigures({ page: 1, sort: selectedSortFilter });

			if (currentPage !== 1) {
				setCurrentPage(1);
			}
		}
	};

	const handleSearchOnChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
		setSearch(event.target.value);

		if (currentPage !== 1) {
			setCurrentPage(1);
		}
	};

	const handlePageChange = (page: number): void => {
		if (page !== currentPage) {
			setCurrentPage(page);
			handleFetchMapsFigures({ page });
		}
	};

	const handleFetchMapsFigures = async (data?: Partial<MapsFiguresFetchProps>, reset?: boolean): Promise<void> => {
		const selectedPage = reset ? 1 : data?.page || currentPage;
		const selectedSort = reset ? { id: '', label: '' } : data?.sort || sort;
		const selectedSearch = reset ? '' : data?.searchValue || debouncedSearch;

		const selectedThemeIds = reset ? [] : data?.themeIds || selectedThemes.map((theme) => String(theme.id));
		const selectedKindIds = reset ? [] : data?.kindIds || selectedKinds.map((kind) => String(kind.id));

		scrollToTop();
		setHasFiltered(data !== undefined);
		fetchMapsFigures(selectedPage, selectedSort, selectedSearch, selectedThemeIds, selectedKindIds);
	};

	const mapsFiguresFilters = useMapsFiguresFilters(props.module, (themeIds: string[], kindIds: string[]) =>
		handleFetchMapsFigures({ page: 1, themeIds, kindIds })
	);

	useEffect(() => {
		handleFetchMapsFigures(undefined);
	}, [initialQuery]);

	useEffect(() => {
		setSearchValue(debouncedSearch || null);

		if (debouncedSearch !== null) {
			handleFetchMapsFigures({ page: 1 });
		}
	}, [debouncedSearch]);

	useEffect(() => {
		if (
			!hasFiltered &&
			!initialQuery &&
			(query[THEME_QUERY_KEY] || query[KIND_QUERY_KEY] || query[SEARCH_QUERY_KEY] || query[SORT_QUERY_KEY])
		) {
			setInitialQuery(query);
		}
	}, [query]);

	useEffect(() => {
		if (!!query[SEARCH_QUERY_KEY] && initialQuery?.[SEARCH_QUERY_KEY] !== query[SEARCH_QUERY_KEY]) {
			setSearch(query[SEARCH_QUERY_KEY]);
		}
	}, [query[SEARCH_QUERY_KEY]]);

	const handleModalOpen = (): void => {
		dispatch(setModalOpen(!modal.modalOpen));
	};

	const setModalItems = (items: CheckboxItem[]): void => {
		switch (modal.modalType) {
			case 'themes':
				setSelectedThemesParam(items);
				break;

			default:
				break;
		}
	};

	useEffect(() => {
		const themeOptions = themes.map(({ documentId, attributes }) => ({
			id: documentId,
			label: attributes?.title || ''
		}));

		switch (modal.modalType) {
			case 'themes':
				setModalFilters(selectedThemes);
				setModalOptions(themeOptions);
				break;

			default:
				setModalFilters([]);
				setModalOptions(undefined);
		}
	}, [modal.modalOpen]);

	return (
		<>
			<CheckboxesModal
				modal={modal}
				filters={modalFilters || []}
				options={modalOptions || []}
				setModalOpen={handleModalOpen}
				setModalSelected={setModalItems}
			/>
			<div className={styles.header} ref={overviewRef}>
				<div className="container">
					<h2>{props.module.overviewTitleText}</h2>
					<span className={styles.searchContainer}>
						<Search
							searchValue={search || ''}
							searchPlaceholderText={props.module.searchPlaceholderText}
							handleSearchOnChange={handleSearchOnChange}
							small
						/>
					</span>
				</div>
			</div>

			<div className={styles.overview}>
				<div className="container">
					<div className={styles.overviewContainer}>
						<MobileActions
							filtersIsOpen={filtersIsOpen}
							setFiltersIsOpen={setFiltersIsOpen}
							sortIsOpen={sortIsOpen}
							setSortIsOpen={setSortIsOpen}
						/>

						<div className={styles.filter}>
							<FilterSidebar
								module={props.module}
								mobileIsOpen={filtersIsOpen}
								setMobileIsOpen={setFiltersIsOpen}
								filters={mapsFiguresFilters}
								handleDeleteFilters={handleDeleteFilter}
								handleResetFilter={handleDeleteFilter}
								amountOfActiveFilters={[...selectedThemes, ...selectedKinds].length > 0 ? 1 : 0}
								activeFilters={[...selectedThemes, ...selectedKinds] as IActiveFilter[]}
							/>
						</div>
						<div className={styles.content}>
							<div className={styles.resultsHeader}>
								<h3 id={props.module.overviewTitleText}>{props.module.overviewTitleText}</h3>
								<div className={styles.sortBar}>
									<span id="pageResults" aria-live="polite">
										{formatMessage({ id: 'global.results' }, { results: requestCount })}
									</span>
									<Sort onChange={handleSortOnChange} mobileIsOpen={sortIsOpen} setMobileIsOpen={setSortIsOpen} />
								</div>
							</div>
							<div className={clsx(styles.mapsFigures, requestIsLoading && styles.mapsFiguresLoading)}>
								{requestCount && requestCount > 0 ? (
									requests?.map((mapFigure: MapsFiguresFieldsFragment, index) => (
										<Card
											key={`mapFigure-${mapFigure?.name}-${index}`}
											title={
												mapFigure?.link?.url
													? String(mapFigure?.name)
													: String(
															mapFigure?.media?.data
																? renderDownloadLink(
																		{
																			ext: mapFigure.media.data.attributes?.ext || '',
																			size: mapFigure.media.data.attributes?.size || 0
																		},
																		mapFigure?.name
																  )
																: mapFigure?.name
													  )
											}
											slug={
												mapFigure?.link?.url ||
												mapFigure?.link?.linkUrl ||
												String(mapFigure?.media?.data?.attributes?.url) ||
												''
											}
										/>
									))
								) : (
									<EmptyState onClearFilters={handleDeleteFilter} isLoading={requestIsLoading} />
								)}
							</div>
						</div>
						<div className={styles.pagination}>
							<Pagination totalCount={requestCount} onPageChange={handlePageChange} />
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default MapsFiguresOverview;
